import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Users, Monitor, Trophy, History, Volume2, VolumeX, BookOpen, Download, Share2, Copy, Check, Smartphone, Wifi } from 'lucide-react';
import { usePWA, shareApp, registerServiceWorker } from '../hooks/usePWA';
import { inviteAPI } from '../services/api';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { startGame, soundEnabled, setSoundEnabled, matchHistory, stats, isLoading } = useGame();
  const { isInstallable, isInstalled, installApp } = usePWA();
  
  const [selectedMode, setSelectedMode] = useState(null);
  const [player1Name, setPlayer1Name] = useState('');
  const [player2Name, setPlayer2Name] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [inviterInfo, setInviterInfo] = useState(null);

  // Register service worker on mount
  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Check for invite parameter
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (inviteId) {
      loadInvite(inviteId);
    }
  }, [searchParams]);

  const loadInvite = async (inviteId) => {
    const result = await inviteAPI.getInvite(inviteId);
    if (result.success) {
      setInviterInfo(result.data);
    }
  };

  const handleStartGame = () => {
    if (!selectedMode) return;
    
    const sanitizedP1 = (player1Name || '').trim().slice(0, 20) || 'Player 1';
    const sanitizedP2 = (player2Name || '').trim().slice(0, 20) || 'Player 2';
    
    startGame(selectedMode, sanitizedP1, sanitizedP2);
    navigate('/game');
  };

  const handleInstall = async () => {
    const installed = await installApp();
    if (installed) {
      console.log('App installed successfully!');
    }
  };

  const handleShare = async () => {
    // Create invite first
    const playerName = player1Name.trim() || 'A friend';
    const result = await inviteAPI.createInvite(playerName, 'Come play Book Cricket with me!');
    
    if (result.success) {
      const shareUrl = inviteAPI.getShareUrl(result.data.id);
      setInviteLink(shareUrl);
      
      // Try native share first
      const shareResult = await shareApp({
        title: 'Book Cricket - Challenge!',
        text: `${playerName} is challenging you to Book Cricket! 🏏📖`,
        url: shareUrl
      });
      
      if (shareResult.method === 'clipboard' && shareResult.success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else if (!shareResult.success) {
        // Show dialog with link
        setShowInviteDialog(true);
      }
    } else {
      // Fallback share without invite tracking
      const shareResult = await shareApp();
      if (shareResult.method === 'clipboard' && shareResult.success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink || window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const lastMatch = matchHistory && matchHistory.length > 0 ? matchHistory[0] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 via-emerald-800 to-green-900 relative overflow-hidden">
      {/* Stadium Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] border-4 border-white rounded-b-full" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] border-4 border-white rounded-t-full" />
      </div>

      {/* Floating Cricket Elements */}
      <div className="absolute top-20 left-10 w-4 h-4 bg-red-500 rounded-full animate-bounce opacity-60" />
      <div className="absolute top-40 right-20 w-3 h-3 bg-yellow-400 rounded-full animate-bounce delay-300 opacity-60" />
      <div className="absolute bottom-40 left-20 w-5 h-5 bg-white rounded-full animate-bounce delay-500 opacity-40" />

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Book Cricket</h1>
              <p className="text-emerald-300 text-sm">The Classic Game</p>
            </div>
          </div>
          <div className="flex gap-2">
            {/* Install Button */}
            {isInstallable && !isInstalled && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleInstall}
                className="text-white hover:bg-white/10"
                title="Install App"
              >
                <Download className="w-5 h-5" />
              </Button>
            )}
            {isInstalled && (
              <div className="flex items-center gap-1 text-emerald-300 text-xs px-2">
                <Smartphone className="w-4 h-4" />
                <span>Installed</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="text-white hover:bg-white/10"
              title="Invite Friends"
            >
              {copied ? <Check className="w-5 h-5 text-green-400" /> : <Share2 className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white hover:bg-white/10"
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/history')}
              className="text-white hover:bg-white/10"
            >
              <History className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Invite Banner */}
        {inviterInfo && (
          <Card className="bg-amber-500/20 border-amber-400/50 mb-6 animate-in fade-in slide-in-from-top-4">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">{inviterInfo.inviter_name} invited you!</p>
                  <p className="text-amber-200 text-sm">{inviterInfo.message || 'Ready to play?'}</p>
                </div>
              </div>
              <Button
                onClick={() => setSelectedMode('pvp')}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                Accept Challenge
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Hero Section */}
          <div className="text-center mb-10">
            <div className="inline-block mb-4">
              <div className="relative">
                <div className="w-32 h-40 bg-gradient-to-br from-amber-700 to-amber-900 rounded-lg shadow-2xl mx-auto transform rotate-3 transition-transform hover:rotate-0 duration-300">
                  <div className="absolute inset-2 bg-gradient-to-br from-amber-50 to-amber-100 rounded">
                    <div className="h-full flex flex-col justify-center items-center">
                      <div className="w-12 h-1 bg-amber-300 mb-2" />
                      <div className="w-16 h-1 bg-amber-200 mb-2" />
                      <div className="w-10 h-1 bg-amber-300 mb-2" />
                      <div className="w-14 h-1 bg-amber-200" />
                    </div>
                  </div>
                  <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-amber-900 to-amber-800 rounded-l-lg" />
                </div>
              </div>
            </div>
            <h2 className="text-4xl font-bold text-white mb-3">Flip & Score!</h2>
            <p className="text-emerald-200 text-lg max-w-md mx-auto">
              Turn the pages, score runs, and become the champion! Watch out for 0 and 8 - they get you OUT!
            </p>
          </div>

          {/* Stats Banner */}
          {stats.total_matches > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-6">
              <div className="bg-emerald-800/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{stats.total_matches}</p>
                <p className="text-emerald-400 text-xs">Matches</p>
              </div>
              <div className="bg-emerald-800/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{stats.pvp_matches}</p>
                <p className="text-emerald-400 text-xs">PvP</p>
              </div>
              <div className="bg-emerald-800/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{stats.pvc_matches}</p>
                <p className="text-emerald-400 text-xs">vs CPU</p>
              </div>
              <div className="bg-emerald-800/30 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-400">{stats.highest_score}</p>
                <p className="text-emerald-400 text-xs">High Score</p>
              </div>
            </div>
          )}

          {/* Game Mode Selection */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card 
              className={`cursor-pointer transition-all duration-300 hover:scale-105 border-2 ${
                selectedMode === 'pvp' 
                  ? 'border-amber-400 bg-emerald-800/80 shadow-lg shadow-amber-400/20' 
                  : 'border-emerald-600/50 bg-emerald-800/50 hover:border-emerald-500'
              }`}
              onClick={() => setSelectedMode('pvp')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Local PvP</h3>
                <p className="text-emerald-300 text-sm">Same device with a friend</p>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all duration-300 hover:scale-105 border-2 ${
                selectedMode === 'pvc' 
                  ? 'border-amber-400 bg-emerald-800/80 shadow-lg shadow-amber-400/20' 
                  : 'border-emerald-600/50 bg-emerald-800/50 hover:border-emerald-500'
              }`}
              onClick={() => setSelectedMode('pvc')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Monitor className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">vs Computer</h3>
                <p className="text-emerald-300 text-sm">Test your luck against AI</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer transition-all duration-300 hover:scale-105 border-2 border-emerald-600/50 bg-gradient-to-br from-emerald-700/50 to-teal-700/50 hover:border-teal-400"
              onClick={() => navigate('/online')}
            >
              <CardContent className="p-6 text-center relative overflow-hidden">
                <div className="absolute top-2 right-2">
                  <Badge className="bg-teal-500 text-white text-xs">NEW</Badge>
                </div>
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Wifi className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Play Online</h3>
                <p className="text-emerald-300 text-sm">Challenge friends anywhere!</p>
              </CardContent>
            </Card>
          </div>

          {/* Player Names */}
          {selectedMode && (
            <Card className="bg-emerald-800/50 border-emerald-600/50 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <CardHeader>
                <CardTitle className="text-white">Enter Player Names</CardTitle>
                <CardDescription className="text-emerald-300">Customize your game experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-emerald-200 mb-2 block">Player 1 Name</label>
                  <Input
                    placeholder="Enter name..."
                    value={player1Name}
                    onChange={(e) => setPlayer1Name(e.target.value)}
                    maxLength={20}
                    className="bg-emerald-900/50 border-emerald-600 text-white placeholder:text-emerald-400"
                  />
                </div>
                {selectedMode === 'pvp' && (
                  <div>
                    <label className="text-sm text-emerald-200 mb-2 block">Player 2 Name</label>
                    <Input
                      placeholder="Enter name..."
                      value={player2Name}
                      onChange={(e) => setPlayer2Name(e.target.value)}
                      maxLength={20}
                      className="bg-emerald-900/50 border-emerald-600 text-white placeholder:text-emerald-400"
                    />
                  </div>
                )}
                <div className="flex gap-3">
                  <Button 
                    onClick={handleStartGame}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold py-6 text-lg shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02]"
                  >
                    Start Match
                  </Button>
                  <Button
                    onClick={handleShare}
                    variant="outline"
                    className="border-emerald-500 text-emerald-300 hover:bg-emerald-700/50 py-6"
                  >
                    <Share2 className="w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Last Match Card */}
          {lastMatch && (
            <Card className="bg-emerald-800/30 border-emerald-600/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-amber-400" />
                    <span className="text-emerald-200 text-sm">Last Match Winner:</span>
                    <span className="text-white font-semibold">{lastMatch.winner}</span>
                  </div>
                  <div className="text-emerald-300 text-sm">
                    {lastMatch.player1?.score} - {lastMatch.player2?.score}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rules Card */}
          <Card className="bg-emerald-800/30 border-emerald-600/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-400" />
                How to Play
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-emerald-200 text-sm">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center text-xs text-white shrink-0 mt-0.5">1</span>
                  <span>Flip the book to get a random page number</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center text-xs text-white shrink-0 mt-0.5">2</span>
                  <span>Last digit of the page = Your runs (1-9)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white shrink-0 mt-0.5">!</span>
                  <span>If last digit is <strong>0</strong> or <strong>8</strong> - You're OUT!</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center text-xs text-white shrink-0 mt-0.5">★</span>
                  <span>Highest score wins the match!</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* PWA Install Prompt for Mobile */}
          {isInstallable && !isInstalled && (
            <Card className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-blue-400/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Download className="w-6 h-6 text-blue-400" />
                    <div>
                      <p className="text-white font-medium">Install Book Cricket</p>
                      <p className="text-blue-200 text-sm">Play anytime, even offline!</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleInstall}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    Install
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="bg-emerald-800 border-emerald-600 text-white">
          <DialogHeader>
            <DialogTitle>Invite Friends</DialogTitle>
            <DialogDescription className="text-emerald-300">
              Share this link with your friends to challenge them!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={inviteLink || window.location.origin}
                readOnly
                className="bg-emerald-900 border-emerald-600 text-white"
              />
              <Button onClick={handleCopyLink} variant="outline" className="border-emerald-500">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Challenge me to Book Cricket! 🏏📖 ${inviteLink || window.location.origin}`)}`, '_blank')}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                WhatsApp
              </Button>
              <Button
                onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Challenge me to Book Cricket! 🏏📖 ${inviteLink || window.location.origin}`)}`, '_blank')}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                Twitter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
